import debug from "debug";

const log = debug("app:3d");

/**
 * Handles enabling and disabling 3D mode in the application.
 * This class manages the camera controls and updates UI behavior based on user selections.
 * @category Experience
 * @subcategory World
 */
class Enable3DMode {
  /**
   * Constructor to initialize the Enable3DMode class.
   * @param {Object} experience - The main experience object containing camera, event hub, and other components.
   */
  constructor(experience) {
    /**
     * Reference to the main experience object.
     * @type {Object}
     */
    this.experience = experience;

    /**
     * Event hub for handling application events.
     * @type {Object}
     */
    this.eventHub = experience.eventHub;

    /**
     * Controls for managing the camera.
     * @type {Object}
     */
    this.cameraControls = experience.camera.controls;

    /**
     * Indicates whether 3D mode is active.
     * @type {boolean}
     */
    this.is3DModeActive = false;

    /**
     * Indicates whether an object is selected in the scene.
     * @type {boolean}
     */
    this.isObjectSelected = false;

    /**
     * Flag indicating if 3D mode is still under development.
     * @type {boolean}
     */
    this.comingSoon = false;

    // Ensure rotation is disabled by default
    this.cameraControls.enableRotate = false;
    this.cameraControls.enableDamping = false;

    this._handle3DSwitchBound = this.handle3DSwitch.bind(this);
    this._handleSelectionUpdatedBound = this.handleSelectionUpdated.bind(this);
    this.setupListeners();
  }

  cleanup() {
    this.eventHub.off("3d-switch", this._handle3DSwitchBound);
    this.eventHub.off("selectionUpdated", this._handleSelectionUpdatedBound);
    this.is3DModeActive = false;
    this.isObjectSelected = false;
    this.cameraControls.enableRotate = false;
    this.cameraControls.enableDamping = false;
  }

  /**
   * Sets up event listeners for 3D mode toggle and selection updates.
   */
  setupListeners() {
    this.eventHub.on("3d-switch", this._handle3DSwitchBound);
    this.eventHub.on("selectionUpdated", this._handleSelectionUpdatedBound);
  }

  /**
   * Handles the 3D mode switch toggle event.
   * @param {Object} event - The event object containing the 3D mode state.
   */
  handle3DSwitch(event) {
    log("event in 3D", event.enabled);
    this.is3DModeActive = event.enabled;

    // Check if the feature is marked as "Coming Soon"
    if (this.comingSoon) {
      if (this.is3DModeActive) {
        // Show the "Coming Soon" modal and revert switch state
        this.eventHub.emit("modalInfo", {
          title: "Coming Soon",
          message:
            "3D Mode is currently under development and will be available soon.",
          actions: [
            {
              label: "OK",
              callback: () => {
                // Set the toggle switch back to false when user clicks OK
                const switch3D = document.getElementById("3d-switch");
                if (switch3D) {
                  switch3D.checked = false; // Set it to false
                  // Emit event to revert the 3D mode state
                  this.eventHub.emit("3d-switch", { enabled: false });
                }
              },
            },
          ],
        });

        return; // Stop further execution if 3D mode is not available
      }
    }

    // If comingSoon is false, continue with normal logic
    // If "Coming Soon" is off and 3D is selected, emit the event to switch to 3D
    if (this.is3DModeActive) {
      this.eventHub.emit("3d-enabled", { enabled: true }); // Event to switch display to 3D
    } else {
      this.eventHub.emit("3d-enabled", { enabled: false }); // Event to revert to 2D
      this.eventHub.emit("viewChange", "top");
    }
    this.updateCameraControlState();
    this.sync3DToggles();
  }

  /**
   * Keeps all 3D mode toggles in sync (header 2D/3D, Misc checkbox, etc.).
   */
  sync3DToggles() {
    const enabled = this.is3DModeActive;
    const headerToggle = document.getElementById("header-3d-toggle");
    if (headerToggle) {
      headerToggle.setAttribute("data-mode-3d", enabled ? "true" : "false");
    }
    const miscCheckbox = document.getElementById("3d-switch");
    if (miscCheckbox) {
      miscCheckbox.checked = enabled;
    }
  }

  /**
   * Handles updates when the selection state changes.
   * @param {Object} event - The event object containing the selected objects.
   */
  handleSelectionUpdated(event) {
    this.isObjectSelected =
      Array.isArray(event.selectedObjects) && event.selectedObjects.length > 0;
    this.updateCameraControlState();
  }

  /**
   * Updates the camera controls state based on the current mode and selection state.
   */
  updateCameraControlState() {
    const shouldEnableRotate = this.is3DModeActive;

    if (shouldEnableRotate) {
      this.cameraControls.enableRotate = true;
      this.cameraControls.enableDamping = false;
    } else {
      this.cameraControls.enableRotate = false;
      this.cameraControls.enableDamping = false;
      this.cameraControls.update(); // Ensure the camera stops rotating immediately
    }
  }
}

export default Enable3DMode;
